import { Ketcher } from "ketcher-core";
import { ButtonsConfig } from "ketcher-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface KetcherChangeEventSubscription {
  unsubscribe: () => void;
}
interface UseKetcherProps {
  onStructureDidChange?: (smiles: string) => void;
}

export const useKetcher = (props?: UseKetcherProps) => {
  const onStructureDidChange = props?.onStructureDidChange;
  const ketcherInstanceRef = useRef<Ketcher | null>(null);
  const [isKetcherInitialized, setIsKetcherInitialized] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const initKetcherHandler = useCallback(async (ketcher: Ketcher) => {
    ketcherInstanceRef.current = ketcher;
    if (typeof window !== 'undefined') {
        (window as any).ketcher = ketcher;
    }

    await new Promise((resolve) => setTimeout(resolve, 0));
    setIsKetcherInitialized(true);
  }, []);

  useEffect(() => {

    if (!isKetcherInitialized || !ketcherInstanceRef.current || !onStructureDidChange) {
      return;
    }

    const ketcher = ketcherInstanceRef.current;
    let subscription: KetcherChangeEventSubscription | null = null;
    const eventNameForEventBus = 'change';

    const handleChange = async () => {
      if (ketcherInstanceRef.current) {
        try {
          const smiles = await ketcherInstanceRef.current.getSmiles();
          onStructureDidChange(smiles);
        } catch (error) {
          console.error('Error getting SMILES after change event:', error);
        }
      }
    };

    if (ketcher.changeEvent && typeof ketcher.changeEvent.subscribe === 'function') {
      subscription = ketcher.changeEvent.subscribe(handleChange);
    }

    else if (ketcher.eventBus && typeof ketcher.eventBus.on === 'function') {
      ketcher.eventBus.on(eventNameForEventBus, handleChange);
    } else {
      console.warn('Ketcher changeEvent.subscribe or eventBus.on not available for auto-updates from editor content changes.');
    }

    return () => {
      if (subscription?.unsubscribe) {
        subscription.unsubscribe();
      }
      if (ketcher.eventBus && typeof ketcher.eventBus.off === 'function') {
        ketcher.eventBus.off(eventNameForEventBus, handleChange);
      }
    };
  }, [isKetcherInitialized, onStructureDidChange]);

  const setMoleculeOnKetcher = useCallback(async (structureString: string) => {
    if (ketcherInstanceRef.current && isKetcherInitialized) {
      try {
        await ketcherInstanceRef.current.setMolecule(structureString);
      } catch (error) {
        console.error("Ketcher setMolecule error:", error);
        throw error;
      }
    } else {
      const errMessage = "Ketcher instance is not available or not initialized for setMolecule.";
      console.error(errMessage);
      throw new Error(errMessage);
    }
  }, [isKetcherInitialized]);

  const getSmilesFromKetcher = useCallback(async (): Promise<string> => {
    if (ketcherInstanceRef.current && isKetcherInitialized) {
      try {
        return await ketcherInstanceRef.current.getSmiles();
      } catch (error) {
        console.error("Ketcher getSmiles error:", error);
        throw error;
      }
    }
    console.warn("Ketcher not ready for getSmiles, returning empty string.");
    return "";
  }, [isKetcherInitialized]);

  const addFragmentToKetcher = useCallback(async (structureString: string) => {
    if (ketcherInstanceRef.current && isKetcherInitialized) {
      try {
        await ketcherInstanceRef.current.addFragment(structureString);
      } catch (error) {
        console.error("Ketcher addFragment error:", error);
        const errorMessage = error instanceof Error ? error.message : String(error);
        alert(`Error adding fragment: ${errorMessage}`);
        throw error;
      }
    } else {
      const errMessage = "Ketcher instance is not available for addFragment.";
      console.error(errMessage);
      alert(errMessage);
      throw new Error(errMessage);
    }
  }, [isKetcherInitialized]);

  const getMolfileFromKetcher = useCallback(async (): Promise<string> => {
    if (ketcherInstanceRef.current && isKetcherInitialized) {
        try {
            return await ketcherInstanceRef.current.getMolfile();
        } catch (error) {
            console.error("Ketcher getMolfile error:", error);
            throw error;
        }
    }
    console.warn("Ketcher not ready for getMolfile, returning empty string.");
    return "";
  }, [isKetcherInitialized]);


  const highlightAtomsByIds = useCallback(async (atomIds: number[]) => {
    if (ketcherInstanceRef.current?.editor && isKetcherInitialized) {
      try {
        ketcherInstanceRef.current.editor.selection(null);
        if (atomIds.length > 0) {
          ketcherInstanceRef.current.editor.selection({ atoms: atomIds });
        }
      } catch (error) {
        console.error("Ketcher highlightAtomsByIds error:", error);
        throw error;
      }
    } else {
      const errMessage = "Ketcher instance or editor is not available for highlightAtomsByIds.";
      console.error(errMessage);
      throw new Error(errMessage);
    }
  }, [isKetcherInitialized]);

  const ketcherButtons = useMemo((): ButtonsConfig => {
    const currentSearchParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
    const hiddenButtonsParam = currentSearchParams.get("hiddenControls");

    const defaultHiddenButtons = [
        "save", "cut", "zoom-in", "zoom-out", "recognize", "miew",
        "settings", "help", "about", "layout", "clean", "arom", "dearom",
        "cip", "check", "analyse", "text", "enhanced-stereo", "rgroup-attpoints",
        "rgroup", "rgroup-label", "rgroup-fragment",
        "shape", "shape-ellipse", "shape-rectangle", "shape-line",
        "reaction-mapping-tools", "reaction-automap", "reaction-map", "reaction-unmap",
        "fullscreen", "sgroup", "reaction-plus", "arrows",
        "reaction-arrow-open-angle", "reaction-arrow-filled-triangle",
        "reaction-arrow-filled-bow", "reaction-arrow-dashed-open-angle",
        "reaction-arrow-failed", "reaction-arrow-both-ends-filled-triangle",
        "reaction-arrow-equilibrium-filled-half-bow", "reaction-arrow-equilibrium-filled-triangle",
        "reaction-arrow-equilibrium-open-angle", "reaction-arrow-unbalanced-equilibrium-filled-half-bow",
        "reaction-arrow-unbalanced-equilibrium-open-half-angle",
        "reaction-arrow-unbalanced-equilibrium-large-filled-half-bow",
        "reaction-arrow-unbalanced-equilibrium-filled-half-triangle",
        "reaction-arrow-elliptical-arc-arrow-filled-bow", "reaction-arrow-elliptical-arc-arrow-filled-triangle",
        "reaction-arrow-elliptical-arc-arrow-open-angle", "reaction-arrow-elliptical-arc-arrow-open-half-angle",
      ];

    const allHiddenButtons = hiddenButtonsParam
      ? [...defaultHiddenButtons, ...hiddenButtonsParam.split(",").map(s => s.trim()).filter(s => s)]
      : defaultHiddenButtons;

    return allHiddenButtons.reduce<ButtonsConfig>((acc, button) => {
      if (button) acc[button] = { hidden: true };
      return acc;
    }, {} as ButtonsConfig);
  }, []);

  return {
    wrapperRef,
    isKetcherInitialized,
    initKetcherHandler,
    setMoleculeOnKetcher,
    getSmilesFromKetcher,
    getMolfileFromKetcher,
    ketcherButtons,
    highlightAtomsByIds,
    addFragmentToKetcher,
  };
};